import React, { FC, ReactElement } from 'react'
import { DeviceModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import FormRow from '../../Common/Components/FormRow'
import { OutlinedPasswordTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedPasswordTextInput'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import type { DeviceModalCapabilityFlags } from '../Spec/DeviceModalCapabilityKeys'
import {
    DEVICE_MODAL_CAPABILITY_BROADWORKS_ID_OR_UPN_WEBEX_USER_ID,
    DEVICE_MODAL_CAPABILITY_CISCO_COMMON_IDENTITY,
    DEVICE_MODAL_CAPABILITY_DISABLE_REALTIME_QUEUE_MONITORING,
    DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DNIS_ASSOCIATIONS,
    DEVICE_MODAL_CAPABILITY_INT_DIALLING_ADDRESS,
    DEVICE_MODAL_CAPABILITY_MS_TEAMS_USER_ID,
    DEVICE_MODAL_CAPABILITY_OWNING_GROUP_ID,
    DEVICE_MODAL_CAPABILITY_OVERRIDE_MONITORING,
    DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS,
    DEVICE_MODAL_CAPABILITY_TELSYS_MONITOR_PASSWORD
} from '../Spec/DeviceModalCapabilityKeys'
import {
    DEVICE_DISABLE_RT_MONITORING_FIELD,
    DEVICE_ID_ON_TELSYS_FIELD,
    DEVICE_ID_ON_TELSYS_OWNER_FIELD,
    DEVICE_NUMBER_INTERNAL_FIELD,
    DEVICE_OVERRIDE_MONITORING_FIELD,
    DEVICE_PRIMARY_EMAIL_FIELD,
    DEVICE_TELSYS_PASSWORD_FIELD,
    DEVICE_TELSYS_USER_ID_FIELD
} from '../Constants/FieldNames'

type Props = {
    viewOnly?: boolean
    capabilityFlags: DeviceModalCapabilityFlags
    /** Admin list telephony module type; drives telephony identity labels and field visibility. */
    telSysType?: TelSysTypes
}

/**
 * Resolves the i18n key for the ID-on-telephony-server field label on the Advanced tab.
 *
 * @param telSysType - Telephony module from the admin list scope.
 * @returns Bundle key for {@link CLanguageHandler.get}.
 */
const resolveIdOnTelSysLabelKey = (telSysType?: TelSysTypes): string | number => {
    if (telSysType === TelSysTypes.BROADSOFT_BROADWORKS) return DeviceModal.DeviceModal_General_IDOnTelSys_Broadworks

    if (telSysType === TelSysTypes.WEBEX_CALLING) return DeviceModal.DeviceModal_General_IDOnTelSys_Webex

    if (telSysType === TelSysTypes.MS_TEAMS) return DeviceModal.DeviceModal_General_IDOnTelSys_MsTeams

    if (telSysType === TelSysTypes.PANASONIC_TDA_NCP) return DeviceModal.DeviceModal_General_IDOnTelSys_Panasonic

    if (telSysType === TelSysTypes.BROADSOFT_M6) return DeviceModal.DeviceModal_General_IDOnTelSys_M6

    return DeviceModal.DeviceModal_General_IDOnTelSys
}

/**
 * Resolves the i18n key for the telephony user id field label on the Advanced tab.
 *
 * @param telSysType - Telephony module from the admin list scope.
 * @returns Bundle key for {@link CLanguageHandler.get}.
 */
const resolveTelSysUserIdLabelKey = (telSysType?: TelSysTypes): string | number => {
    if (telSysType === TelSysTypes.WEBEX_CALLING)
        return DeviceModal.DeviceModal_General_TelSysUserID_WebexCiscoCommonIdentity

    if (telSysType === TelSysTypes.MS_TEAMS) return DeviceModal.DeviceModal_General_TelSysUserID_MsTeamsUpnUrn

    return DeviceModal.DeviceModal_General_TelSysUserID
}

/**
 * Advanced tab for the device configuration modal.
 *
 * @param props - Tab props.
 * @returns Advanced device fields (telephony identity, credentials, monitoring flags) for add/change/view.
 */
export const DeviceAdvancedTab: FC<Props> = ({ viewOnly, capabilityFlags, telSysType }: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    const disabled: boolean = viewOnly ?? false
    const showIdOnTelSys: boolean = capabilityFlags[DEVICE_MODAL_CAPABILITY_BROADWORKS_ID_OR_UPN_WEBEX_USER_ID]
    const showIdOnTelSysOwner: boolean = capabilityFlags[DEVICE_MODAL_CAPABILITY_OWNING_GROUP_ID]
    const showInternalExtension: boolean = capabilityFlags[DEVICE_MODAL_CAPABILITY_INT_DIALLING_ADDRESS]
    const showTelSysCredentialRow: boolean =
        capabilityFlags[DEVICE_MODAL_CAPABILITY_MS_TEAMS_USER_ID] ||
        capabilityFlags[DEVICE_MODAL_CAPABILITY_CISCO_COMMON_IDENTITY]
    const showTelSysMonitorPassword: boolean = capabilityFlags[DEVICE_MODAL_CAPABILITY_TELSYS_MONITOR_PASSWORD]
    const showPrimaryEmail: boolean = capabilityFlags[DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS]
    /**
     * MS Teams {@code Call Queue} row: DNIS and disable real-time queue monitoring are both enabled in the matrix.
     * Override monitoring is hidden for that presentation only; all other device types keep the checkbox when the
     * matrix allows it.
     */
    const isMsTeamsCallQueueCapabilitySet: boolean =
        capabilityFlags[DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DNIS_ASSOCIATIONS] &&
        capabilityFlags[DEVICE_MODAL_CAPABILITY_DISABLE_REALTIME_QUEUE_MONITORING]
    const showOverrideMonitoring: boolean =
        capabilityFlags[DEVICE_MODAL_CAPABILITY_OVERRIDE_MONITORING] &&
        telSysType !== TelSysTypes.MS_TEAMS &&
        telSysType !== TelSysTypes.WEBEX_CALLING
    /** MS Teams Call Queue row only (DNIS plus disable real-time queue monitoring in the matrix). */
    const showDisableRTMonitoring: boolean = isMsTeamsCallQueueCapabilitySet

    return (
        <div className='device-advanced-tab tab-area'>
            {showIdOnTelSys ? (
                <FormRow>
                    <OutlinedTextInput
                        inputName={DEVICE_ID_ON_TELSYS_FIELD}
                        labelLeft={language.get(resolveIdOnTelSysLabelKey(telSysType))}
                        disabled={disabled}
                    />
                </FormRow>
            ) : null}

            {showIdOnTelSysOwner ? (
                <FormRow>
                    <OutlinedTextInput
                        inputName={DEVICE_ID_ON_TELSYS_OWNER_FIELD}
                        labelLeft={language.get(DeviceModal.DeviceModal_General_IDOnTelSysOwner)}
                        disabled={disabled}
                    />
                </FormRow>
            ) : null}

            {showInternalExtension ? (
                <FormRow>
                    <OutlinedTextInput
                        inputName={DEVICE_NUMBER_INTERNAL_FIELD}
                        labelLeft={language.get(DeviceModal.DeviceModal_General_InternalExtension)}
                        disabled={disabled}
                    />
                </FormRow>
            ) : null}

            {showTelSysCredentialRow ? (
                <FormRow>
                    <OutlinedTextInput
                        inputName={DEVICE_TELSYS_USER_ID_FIELD}
                        labelLeft={language.get(resolveTelSysUserIdLabelKey(telSysType))}
                        disabled={disabled}
                    />
                </FormRow>
            ) : null}

            {showTelSysMonitorPassword ? (
                <FormRow>
                    <OutlinedPasswordTextInput
                        inputName={DEVICE_TELSYS_PASSWORD_FIELD}
                        labelLeft={language.get(DeviceModal.DeviceModal_General_TelSysMonitorPassword)}
                        disabled={disabled}
                    />
                </FormRow>
            ) : null}

            {showPrimaryEmail ? (
                <FormRow>
                    <OutlinedTextInput
                        inputName={DEVICE_PRIMARY_EMAIL_FIELD}
                        labelLeft={language.get(DeviceModal.DeviceModal_General_PrimaryEmail)}
                        disabled={disabled}
                    />
                </FormRow>
            ) : null}

            {showOverrideMonitoring ? (
                <FormRow>
                    <RouterCheckbox
                        name={DEVICE_OVERRIDE_MONITORING_FIELD}
                        labelLeft={language.get(DeviceModal.DeviceModal_Monitoring_OverrideMonitoring)}
                        disabled={disabled}
                    />
                </FormRow>
            ) : null}

            {showDisableRTMonitoring ? (
                <FormRow>
                    <RouterCheckbox
                        name={DEVICE_DISABLE_RT_MONITORING_FIELD}
                        labelLeft={language.get(DeviceModal.DeviceModal_Monitoring_DisableRTMonitoring)}
                        disabled={disabled}
                    />
                </FormRow>
            ) : null}
        </div>
    )
}
